/**
 * Mix-safety primitives for the layered audio system (LOCKED-DESIGN 18.4):
 * the priority-bus ducker (critical-overflow alarms and red-orb fuse cues
 * ALWAYS take priority over music, per 2.2) and the steal-oldest voice cap.
 * Pure logic — no WebAudio — so the contract is unit-testable headlessly.
 */

/** Music-bus gain target while the critical-overflow alarm loop runs. */
export const ALARM_DUCK = 0.22;
/** Music-bus gain target while red-orb fuse ticks are landing. */
export const FUSE_DUCK = 0.5;

export class PriorityDucker {
  private alarm = false;
  private fuseHoldUntil = -Infinity;

  setAlarm(active: boolean): void {
    this.alarm = active;
  }

  get alarmActive(): boolean {
    return this.alarm;
  }

  /**
   * A fuse tick landed at `now`. Ticks arrive at most one second apart, so a
   * hold slightly over a second keeps the duck engaged across a live fuse and
   * releases automatically once the orb detonates or is defused.
   */
  fusePulse(now: number, holdSeconds = 1.05): void {
    this.fuseHoldUntil = Math.max(this.fuseHoldUntil, now + holdSeconds);
  }

  /** Target gain for the music bus (1 = no duck). Alarm outranks fuse. */
  duckTarget(now: number): number {
    if (this.alarm) return ALARM_DUCK;
    if (now < this.fuseHoldUntil) return FUSE_DUCK;
    return 1;
  }
}

/**
 * Global polyphony guard: registering a voice past the cap returns the oldest
 * live handle so the caller can steal it (fast fade + stop). Callers prune by
 * end time each frame so finished voices never count against the cap.
 */
export class VoiceCap<T> {
  private readonly entries: Array<{ handle: T; ends: number }> = [];

  constructor(readonly max: number) {}

  get active(): number {
    return this.entries.length;
  }

  prune(now: number): void {
    for (let index = this.entries.length - 1; index >= 0; index--) {
      if (this.entries[index]!.ends <= now) this.entries.splice(index, 1);
    }
  }

  /** Register a voice ending at `ends`; returns the stolen oldest handle when over cap. */
  register(handle: T, ends: number): T | null {
    let stolen: T | null = null;
    if (this.entries.length >= this.max) stolen = this.entries.shift()?.handle ?? null;
    this.entries.push({ handle, ends });
    return stolen;
  }
}
